import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";

const LogoEditor = () => {
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Inline styles
  const styles = {
    container: {
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 0 20px rgba(0,0,0,0.05)",
      padding: "30px",
      maxWidth: "800px",
      margin: "20px auto",
    },
    header: {
      borderBottom: "1px solid #eaeaea",
      paddingBottom: "15px",
      marginBottom: "25px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    heading: {
      fontSize: "24px",
      fontWeight: "600",
      color: "#333",
      margin: "0",
    },
    section: {
      backgroundColor: "#f9f9f9",
      borderRadius: "6px",
      padding: "20px",
      marginBottom: "25px",
      borderLeft: "4px solid #007bff",
    },
    sectionTitle: {
      fontSize: "18px",
      fontWeight: "500",
      marginBottom: "20px",
      color: "#444",
    },
    formGroup: {
      marginBottom: "20px",
    },
    input: {
      width: "100%",
      padding: "10px 15px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      fontSize: "14px",
      transition: "border-color 0.2s ease",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "500",
      fontSize: "14px",
      color: "#555",
    },
    actionButtons: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "15px",
      marginTop: "30px",
    },
    saveButton: {
      backgroundColor: "#28a745",
      color: "white",
      border: "none",
      padding: "12px 24px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: "500",
      transition: "background-color 0.2s ease",
      position: "relative",
    },
    loading: {
      textAlign: "center",
      padding: "50px 0",
      fontSize: "18px",
      color: "#777",
    },
    error: {
      backgroundColor: "#f8d7da",
      color: "#721c24",
      padding: "15px",
      borderRadius: "4px",
      marginBottom: "20px",
      fontSize: "16px",
    },
    previewSection: {
      marginTop: "20px",
      textAlign: "center",
    },
    previewImage: {
      maxWidth: "200px",
      maxHeight: "80px",
      border: "1px dashed #ddd",
      padding: "10px",
      borderRadius: "4px",
    },
    saveButtonText: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    spinner: {
      width: "16px",
      height: "16px",
      border: "2px solid rgba(255,255,255,0.3)",
      borderRadius: "50%",
      borderTop: "2px solid #fff",
      animation: "spin 1s linear infinite",
    },
  };

  useEffect(() => {
    fetchLogoData();
  }, []);

  const fetchLogoData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/footer-section"
      );
      if (response.data.success) {
        // Only extract the logo URL from the response
        const { logoUrl } = response.data.data.middleSection;
        setLogoUrl(logoUrl);
      } else {
        setError("Failed to fetch logo data");
        toast.error("Failed to fetch logo data");
      }
    } catch (err) {
      console.error("Error fetching logo data:", err);
      setError("Error fetching logo data");
      toast.error("Error fetching logo data: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    setLogoUrl(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!logoUrl.trim()) {
      toast.warning("Please enter a logo URL");
      return;
    }
    
    try {
      setSaving(true);
      // First, get the current footer data
      const currentDataResponse = await axios.get(
        "https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/footer-section"
      );
      if (!currentDataResponse.data.success) {
        throw new Error("Failed to fetch current footer data");
      }
      
      // Update only the logo URL in the current data
      const updatedData = {
        ...currentDataResponse.data.data,
        middleSection: {
          ...currentDataResponse.data.data.middleSection,
          logoUrl: logoUrl
        }
      };
      
      // Send the update with only the logo URL changed
      const response = await axios.put(
        "https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/footer-section",
        updatedData
      );
      
      if (response.data.success) {
        toast.success("Logo updated successfully!");
      } else {
        toast.error("Failed to update logo: " + (response.data.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Error updating logo:", err);
      toast.error("Error updating logo: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div style={styles.loading}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only"></span>
        </div>
        <p>Loading logo data...</p>
      </div>
    );
    
  if (error)
    return (
      <div style={styles.error}>
        <i className="fas fa-exclamation-circle mr-2"></i>
        Error: {error}
      </div>
    );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.heading}>Logo Editor</h2>
      </div>
      <form onSubmit={handleSubmit}>
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Logo Settings (Header & Footer Logo)</h3>
          <div style={styles.formGroup}>
            <label style={styles.label}>Logo URL:</label>
            <input
              type="text"
              value={logoUrl}
              onChange={handleLogoChange}
              style={styles.input}
              placeholder="Enter logo URL"
            />
          </div>
          {logoUrl && (
            <div style={styles.previewSection}>
              <h4>Logo Preview:</h4>
              <img
                src={logoUrl}
                alt="Logo preview"
                style={styles.previewImage}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://placehold.co/200x80?text=Invalid+Image+URL";
                }}
              />
            </div>
          )}
        </div>
        <div style={styles.actionButtons}>
          <button type="submit" style={styles.saveButton} disabled={saving}>
            <div style={styles.saveButtonText}>
              {saving && <span style={styles.spinner}></span>}
              {saving ? "Saving..." : "Update Logo"}
            </div>
          </button>
        </div>
      </form>
      {/* Add CSS for spinner animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LogoEditor;